.screen {
	min-height: 100%;
	padding: var(--space-5) var(--space-5) var(--space-7);
	max-width: var(--screen-max);
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: var(--space-5);
}

.empty {
	padding: var(--space-5) 0;
	margin: 0;
	color: var(--ink-muted);
	text-align: center;
}

/* Full-bleed hero container. Holds one of: uploaded photo, place map, or type
   emoji — plus the overlaid upload control (which needs this to be relative). */
.heroWrap {
	position: relative;
	width: calc(100% + 2 * var(--space-5));
	margin: 0 calc(-1 * var(--space-5));
	height: 240px;
	overflow: hidden;
	background: var(--surface-input);
}

.heroImg {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

.heroMapInner {
	height: 100%;
	border-radius: 0;
}

.heroEmojiWrap {
	width: 100%;
	height: 100%;
	background: color-mix(
		in srgb,
		var(--accent-lilac) 22%,
		var(--surface-input)
	);
	display: grid;
	place-items: center;
}

.heroEmoji {
	font-size: 4rem;
}

/* Above the screen max-width the detail card insets from the viewport, so the
   hero is no longer edge-to-edge — round its corners to match the cards. */
@media (min-width: 640px) {
	.heroWrap {
		border-radius: var(--radius-lg);
	}
}

.body {
	display: flex;
	flex-direction: column;
	gap: var(--space-5);
}

/* Header back button — square icon control sized for the header bar. */
.back {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: none;
	background: none;
	border-radius: var(--radius-sm);
	color: var(--ink-muted);
	cursor: pointer;
}

.back:hover {
	color: var(--ink);
	background: var(--surface-input);
}

.nameRow {
	display: flex;
	align-items: center;
	gap: var(--space-3);
	flex-wrap: wrap;
}

.name {
	font-size: 1.5rem;
	font-weight: 700;
	margin: 0;
	color: var(--ink);
	background: none;
	border: 1px solid transparent;
	padding: var(--space-2);
	border-radius: var(--radius-sm);
	font-family: inherit;
}

/* Not while offline, when the body fieldset is disabled. */
.body:not(:disabled) .name:hover {
	border-color: color-mix(in srgb, var(--ink) 12%, transparent);
	cursor: text;
}

.nameInput {
	font-size: 1.5rem;
	font-weight: 700;
	width: 100%;
	padding: var(--space-2);
	border: 1px solid var(--accent-sage);
	border-radius: var(--radius-sm);
	background: var(--surface-input);
	color: var(--ink);
	font-family: inherit;
}

.saveStatus {
	font-size: 0.85rem;
	color: var(--ink-muted);
	transition: opacity 0.2s ease;
}

.saveStatusSaved {
	color: var(--accent-sage);
}

.saveStatusError {
	color: var(--accent-rose);
}

.where {
	margin: 0;
	color: var(--ink-muted);
	font-size: 0.95rem;
}

.whereRow {
	display: flex;
	align-items: center;
	gap: var(--space-3);
	flex-wrap: wrap;
}

.whereRow .where {
	flex: 1 1 auto;
	min-width: 0;
}

.mapLinks {
	display: flex;
	flex-wrap: wrap;
	gap: var(--space-2);
}

.mapLink {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 6px 12px;
	background: color-mix(in srgb, var(--ink) 6%, transparent);
	color: var(--ink-muted);
	border-radius: var(--radius-md);
	font-size: 0.8125rem;
	font-weight: 600;
	text-decoration: none;
}

.mapLink:hover {
	background: color-mix(in srgb, var(--ink) 10%, transparent);
	color: var(--ink);
}

/* Compact enough to sit in the header right slot, level with the toggles on
   the list/map headers. */
.didThis {
	padding: 6px 12px;
	background: var(--accent-sage);
	color: #fff;
	border: none;
	border-radius: var(--radius-md);
	font: inherit;
	font-size: 0.8125rem;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.1s ease;
	white-space: nowrap;
}

.didThis:hover:not(:disabled) {
	background: color-mix(in srgb, var(--accent-sage) 90%, black);
}

.didThis:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.didThisDone {
	background: color-mix(in srgb, var(--ink) 8%, transparent);
	color: var(--ink-muted);
	opacity: 1;
}

.didThisDone:disabled {
	opacity: 1;
}

.lastVisit {
	margin: 0;
	color: var(--ink-muted);
	font-size: 0.85rem;
}

.visitList {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: var(--space-2);
}

.visitList li {
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	padding: var(--space-2) var(--space-3);
	background: var(--surface-card);
	border-radius: var(--radius-sm);
}

.visitDate {
	font-weight: 500;
	color: var(--ink);
}

.visitAbsolute {
	font-size: 0.8rem;
	color: var(--ink-muted);
}

.section {
	display: flex;
	flex-direction: column;
	gap: var(--space-2);
}

.sectionTitle {
	margin: 0;
	font-size: 0.75rem;
	font-weight: 600;
	color: var(--ink-muted);
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.pills {
	display: flex;
	flex-wrap: wrap;
	gap: var(--space-2);
}

.pill {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 6px var(--space-3);
	border-radius: 999px;
	background: var(--surface-input);
	color: var(--ink);
	border: 1px solid color-mix(in srgb, var(--ink) 10%, transparent);
	font: inherit;
	font-size: 0.85rem;
	cursor: pointer;
	transition:
		background 0.1s ease,
		border-color 0.1s ease;
}

.pill:hover:not(.pillActive) {
	border-color: color-mix(in srgb, var(--ink) 22%, transparent);
}

.pillActive {
	background: color-mix(in srgb, var(--accent-sage) 22%, transparent);
	border-color: var(--accent-sage);
	font-weight: 600;
}

.pillAdd {
	display: inline-flex;
	align-items: center;
	padding: 6px var(--space-3);
	border-radius: 999px;
	background: transparent;
	color: var(--ink-muted);
	border: 1px dashed color-mix(in srgb, var(--ink) 22%, transparent);
	font: inherit;
	font-size: 0.85rem;
	cursor: pointer;
	transition: border-color 0.1s ease;
}

.pillAdd:hover {
	border-color: color-mix(in srgb, var(--ink) 38%, transparent);
}

.pillDone {
	display: inline-flex;
	align-items: center;
	padding: 6px var(--space-3);
	border-radius: 999px;
	background: var(--accent-sage);
	color: #fff;
	border: 1px solid var(--accent-sage);
	font: inherit;
	font-size: 0.85rem;
	font-weight: 600;
	cursor: pointer;
}

.notes {
	width: 100%;
	padding: var(--space-3);
	border: 1px solid color-mix(in srgb, var(--ink) 12%, transparent);
	border-radius: var(--radius-md);
	background: var(--surface-input);
	color: var(--ink);
	font: inherit;
	resize: vertical;
}

.notes:focus {
	outline: 2px solid var(--accent-sage);
	outline-offset: 1px;
}

.notesPrompt {
	display: block;
	width: 100%;
	padding: var(--space-2) var(--space-3);
	border: 1px dashed color-mix(in srgb, var(--ink) 18%, transparent);
	border-radius: var(--radius-md);
	background: transparent;
	color: var(--ink-muted);
	font: inherit;
	font-size: 0.9rem;
	text-align: left;
	cursor: pointer;
	transition:
		border-color 0.1s ease,
		background 0.1s ease;
}

.notesPrompt:hover {
	border-color: color-mix(in srgb, var(--ink) 32%, transparent);
	background: color-mix(in srgb, var(--ink) 4%, transparent);
}

.delete {
	align-self: flex-start;
	margin-top: var(--space-4);
	padding: var(--space-2) var(--space-4);
	background: transparent;
	border: 1px solid color-mix(in srgb, var(--accent-rose) 40%, transparent);
	border-radius: var(--radius-md);
	color: var(--accent-rose);
	font: inherit;
	cursor: pointer;
}

.delete:hover {
	background: color-mix(in srgb, var(--accent-rose) 8%, transparent);
}

/* Shown while an activity's tagging is still deferred, matching the callout on
   the activities landing. */
.pending {
	display: flex;
	align-items: center;
	gap: var(--space-3);
	padding: var(--space-3) var(--space-4);
	background: color-mix(in srgb, var(--accent-sage) 12%, var(--surface-card));
	border: 1px solid color-mix(in srgb, var(--accent-sage) 30%, transparent);
	border-radius: var(--radius-md);
}

.pendingText {
	flex: 1;
	min-width: 0;
	margin: 0;
	font-size: 0.85rem;
	color: var(--ink-muted);
}

.pendingAction {
	flex: none;
	padding: 6px var(--space-3);
	border-radius: 999px;
	background: var(--accent-sage);
	color: #fff;
	border: none;
	font: inherit;
	font-size: 0.85rem;
	font-weight: 600;
	cursor: pointer;
}

/* Offline: the body fieldset disables its controls. Pills keep full strength
   because they show the activity's tags; the actions fade. */
.pillAdd:disabled,
.pillDone:disabled,
.notesPrompt:disabled,
.pendingAction:disabled,
.delete:disabled {
	opacity: 0.5;
}
